import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Zap, Smartphone, MapPin, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { toast } from 'sonner';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/zru2z47s_SOFTPOS%20CTA%20LANDING%20PAGE%20IMAGE.png';

const locationOptions = [
  'Customer locations/homes',
  'Markets/events/pop-ups',
  'Delivery/on-the-go',
  'Multiple locations',
];

const volumeOptions = [
  'Under €1,000',
  '€1,000 to €5,000',
  'Over €5,000',
  'Just starting out',
];

const interestOptions = [
  'SoftPOS',
  'MPOS',
  'Not sure - need advice',
  'Want to compare both options',
];

const timelineOptions = [
  'ASAP - need urgent solution',
  'Within 2 weeks',
  'Within a month',
  'Just exploring options',
];

const benefits = [
  {
    icon: Zap,
    title: 'Instant Deployment',
    body: 'Start taking payments today. SoftPOS downloads in minutes, mobile terminals ship next day. No waiting, no complex setup — just instant payment capability wherever you are.',
  },
  {
    icon: Smartphone,
    title: 'No Hardware Barriers',
    body: 'Your phone is your terminal. With SoftPOS, any compatible Android device becomes a professional payment terminal. Lower costs, higher flexibility, maximum convenience.',
  },
  {
    icon: MapPin,
    title: 'Built for Mobile Business',
    body: "Freedom to operate anywhere. Whether you're at a customer's door, market stall or trade appointment, accept payments professionally with reliable connectivity and instant receipts.",
  },
];

const Field = ({ label, required, children, testId }) => (
  <div className="mb-7" data-testid={testId}>
    <label className="block text-sm font-bold text-gray-800 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Radio = ({ name, value, checked, onChange, testId }) => (
  <label
    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
      checked
        ? 'border-[#1F4E79] bg-[#1F4E79]/5 ring-1 ring-[#1F4E79]/20'
        : 'border-gray-200 bg-white hover:border-[#1F4E79]/40 hover:bg-gray-50'
    }`}
    data-testid={testId}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="accent-[#1F4E79]"
    />
    <span className="text-sm text-gray-800">{value}</span>
  </label>
);

const GetStartedSoftpos = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    businessType: '',
    location: '',
    volume: '',
    interest: '',
    timeline: '',
    name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !form.businessType ||
      !form.location ||
      !form.volume ||
      !form.interest ||
      !form.timeline ||
      !form.name ||
      !form.email ||
      !form.phone
    ) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Thanks! We'll be in touch within 24 hours to discuss your mobile payment solution.");
      setForm({
        businessType: '',
        location: '',
        volume: '',
        interest: '',
        timeline: '',
        name: '',
        email: '',
        phone: '',
        company: '',
      });
      setSubmitting(false);
    }, 700);
  };

  return (
    <div className="min-h-screen flex flex-col relative" data-testid="get-started-softpos-page">
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        aria-hidden="true"
      />
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a1929]/80 via-[#0a1929]/65 to-[#163a5f]/75" aria-hidden="true" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />
        <div className="h-20" />

        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/85 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="get-started-softpos-back"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <div className="max-w-4xl mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/40 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.15em] mb-5">
              Free Consultation — No Obligation
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-4" data-testid="gss-title">
              Mobile Payment Solutions,<br />That Move With You
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl font-medium">
              Smartphone payments or dedicated mobile hardware that is perfect for your on the go business. Get your free consultation with expert recommendations.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <form
              onSubmit={handleSubmit}
              className="lg:col-span-8 bg-white rounded-xl shadow-2xl p-7 sm:p-9"
              data-testid="gss-form"
            >
              <Field label="1. What type of business do you operate?" required testId="gss-field-business">
                <input
                  type="text"
                  value={form.businessType}
                  onChange={update('businessType')}
                  placeholder="e.g. delivery service, market stall, mobile salon, field sales, pop-up retail"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                  data-testid="gss-input-business"
                />
              </Field>

              <Field label="2. Where do you typically take payments?" required testId="gss-field-location">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {locationOptions.map((opt, i) => (
                    <Radio
                      key={opt}
                      name="location"
                      value={opt}
                      checked={form.location === opt}
                      onChange={update('location')}
                      testId={`gss-location-${i}`}
                    />
                  ))}
                </div>
              </Field>

              <Field label="3. What's your monthly card payment volume?" required testId="gss-field-volume">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {volumeOptions.map((opt, i) => (
                    <Radio
                      key={opt}
                      name="volume"
                      value={opt}
                      checked={form.volume === opt}
                      onChange={update('volume')}
                      testId={`gss-volume-${i}`}
                    />
                  ))}
                </div>
              </Field>

              <Field label="4. What mobile solution interests you most?" required testId="gss-field-interest">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {interestOptions.map((opt, i) => (
                    <Radio
                      key={opt}
                      name="interest"
                      value={opt}
                      checked={form.interest === opt}
                      onChange={update('interest')}
                      testId={`gss-interest-${i}`}
                    />
                  ))}
                </div>
              </Field>

              <Field label="5. When are you looking to start accepting mobile payments?" required testId="gss-field-timeline">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {timelineOptions.map((opt, i) => (
                    <Radio
                      key={opt}
                      name="timeline"
                      value={opt}
                      checked={form.timeline === opt}
                      onChange={update('timeline')}
                      testId={`gss-timeline-${i}`}
                    />
                  ))}
                </div>
              </Field>

              <div data-testid="gss-field-contact">
                <label className="block text-sm font-bold text-gray-800 mb-3">6. Contact Details</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Contact name *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gss-input-name"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    placeholder="Email address *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gss-input-email"
                  />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update('phone')}
                    placeholder="Phone number *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gss-input-phone"
                  />
                  <input
                    type="text"
                    value={form.company}
                    onChange={update('company')}
                    placeholder="Business name (optional)"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gss-input-company"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="group mt-7 inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="gss-submit"
              >
                {submitting ? 'Sending…' : 'Request Consultation'}</button>

              <p className="text-xs text-gray-500 mt-4">
                <CheckCircle2 size={12} className="inline mr-1 text-emerald-600" />
                Your information is secure and used only to prepare your free mobile-payment consultation.
              </p>
            </form>

            <aside className="lg:col-span-4 space-y-4 lg:pt-16" data-testid="gss-benefits">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    className="rounded-xl p-5 bg-white/10 backdrop-blur-md border border-white/20 shadow-xl hover:bg-white/15 transition-all duration-200"
                    data-testid={`gss-benefit-${i}`}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Icon size={18} className="text-white" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-[15px] font-bold text-white leading-tight">{b.title}</h3>
                    </div>
                    <p className="text-[13px] text-white/85 leading-relaxed">{b.body}</p>
                  </div>
                );
              })}
            </aside>
          </div>
        </main>

        <Footer />
        <BackToTop />
      </div>
    </div>
  );
};

export default GetStartedSoftpos;
